import { gql } from "apollo-boost";
import { enableExperimentalFragmentVariables } from "graphql-tag";

enableExperimentalFragmentVariables();

export const MeasurementRange = gql`
  fragment MeasurementRange on MeasurementRange {
    low
    high
    units
    complexUnits {
      denominator
      numerator
    }
  }
`;
export const MetricValue = gql`
  fragment MetricValue on RMetric {
    value
    exactness
  }
`;
export const Qualifier = gql`
  fragment Qualifier on Qualifier {
    name
    relation {
      name
      id
      type
      multi
      essence
    }
    value {
      name
      shortname
      id
      category {
        id
        name
        shortname
      }
      patientDescription
      description {
        text
      }
      range @include(if: $includeFeatureRange) {
        ...MeasurementRange
      }
      parents @skip(if: $qfLight) {
        features {
          id
          name
          childrenSetType
        }
      }
    }
    negate
    range {
      ...MeasurementRange
    }
    freeText
    source
  }
  ${MeasurementRange}
`;

export const Feature = gql`
  fragment Feature on Feature {
    name
    id
    shortname
    patientDescription
    alias
    hasRefinement
    organSystem {
      id
      name
      shortname
    }
    category {
      id
      name
      shortname
    }
    qualifierSpec @skip(if: $qfLight) {
      relation {
        name
        id
        type
      }
      mandatory
    }
    description {
      url
      text
    }
    range {
      ...MeasurementRange
    }
    inferredParents @skip(if: $qfLight) {
      id
      name
    }
    inferredChildren @skip(if: $qfLight) {
      id
      name
    }
    qualifiers @skip(if: $qfLight) {
      ...Qualifier
    }
    inferredQualifiers @skip(if: $qfLight) {
      ...Qualifier
    }
  }
  ${MeasurementRange}
  ${Qualifier}
`;

export const QualifiedFeature = gql`
  fragment QualifiedFeature on QualifiedFeature {
    id
    name
    negate
    skip
    feature {
      ...Feature
    }
    qualifiers {
      ...Qualifier
    }
    mandatory {
      relation {
        name
        id
        type
      }
      value {
        name
        id
      }
      range {
        ...MeasurementRange
      }
      freeText
    }
  }
  ${Feature}
  ${MeasurementRange}
  ${Qualifier}
`;
export const GroupedFeatureResponse = gql`
  fragment GroupedFeatureResponse on GroupedFeatureResponse {
    feature {
      ...QualifiedFeature
    }
    skip
    source
    group
    validation
  }
  ${QualifiedFeature}
`;

export const CitationData = gql`
  fragment CitationData on CitationData {
    id
    kahunId
    vancouver
    url
    title
    containerTitle
    DOI
  }
`;
export const Citations = gql`
  fragment Citations on Citations {
    totalCount
    citationsData {
      ...CitationData
    }
  }
  ${CitationData}
`;

export const Sheet = gql`
  fragment Sheet on Sheet {
    rows {
      cols
    }
  }
`;
export const DebugSheet = gql`
  fragment DebugSheet on DebugSheet {
    name
    sheet {
      ...Sheet
    }
  }
  ${Sheet}
`;
export const Flag = gql`
  fragment Flag on Flag {
    type
    ref {
      feature {
        name
      }
      negate
    }
  }
`;
export const NextStep = gql`
  fragment NextStep on NextStep {
    suggestedFeatures {
      qualifiedFeature {
        ...QualifiedFeature
      }
      sumDiseaseGivenFinding
      sumLiftScores
      liftScores {
        liftScore
        qualifiedFeature {
          id
        }
      }
      category
    }
    debugSheet {
      ...Sheet
    }
  }
  ${QualifiedFeature}
  ${Sheet}
`;
export const PatientSuggestions = gql`
  fragment PatientSuggestions on PatientSuggestions {
    debugSheet @include(if: $withDebug) {
      ...Sheet
    }
    suggestions {
      suggestion {
        ...QualifiedFeature
      }
      entropy
      mutualInformation
      mutualInformationUrgencyDifferentiation
      findingProbability
      findingEntropy
      findingLeakAdjustedEntropy
      findingLeak
      category
      inPatient
      inPatientId
      perDiseaseMetrics @include(if: $withDiseaseMetrics) {
        suggestion {
          qualifiers {
            ...Qualifier
          }
        }
        specificity

        pairMetrics {
          sensitivity {
            ...MetricValue
          }
        }

        diseaseIndex
        findingGivenDisease
        adjustedBinaryEntropy
      }
    }
    clustered {
      title {
        ...Feature
      }
      items {
        suggestion {
          ...QualifiedFeature
        }
        entropy
        mutualInformation
        mutualInformationUrgencyDifferentiation
        perDiseaseMetrics @include(if: $withDiseaseMetrics) {
          suggestion {
            qualifiers {
              ...Qualifier
            }
          }
          diseaseIndex
          findingGivenDisease
        }
      }
      category
    }
  }
  ${Feature}
  ${QualifiedFeature}
  ${Sheet}
  ${Qualifier}
  ${MetricValue}
`;
export const PatientSuggestionMIExplanationsFragment = gql`
  fragment PatientSuggestionMIExplanations on PatientSuggestion {
    perDiseaseMetrics {
      diseaseIndex
      findingGivenDisease
      adjustedBinaryEntropy
      specificity
      likelihoodRatioNeg
      likelihoodRatioPos
      pairMetrics {
        sensitivity {
          ...MetricValue
        }
        ppv {
          ...MetricValue
        }
        incidence {
          ...MetricValue
        }
        rrisk_cause {
          ...MetricValue
        }
        rrisk_effect {
          ...MetricValue
        }
      }
      suggestion {
        ...QualifiedFeature
      }
    }
    bestSubtype {
      ...QualifiedFeature
    }
    findingProbability
    findingEntropy
    findingLeak
    findingLeakAdjustedEntropy
    defaultFindingGivenDisease
    defaultFindingGivenDiseaseAdjustedEntropy
  }
  ${MetricValue}
`;

export const ConversationDDFragment = gql`
  fragment ConversationDD on DDResponse {
    allFindings {
      ...QualifiedFeature
    }
    clinicalReasoning {
      id
      inInitialDisorders
      forcedInSort
      disease {
        ...QualifiedFeature
        urgencyInfo: feature {
          id
          urgency
          urgencyId
        }
      }
      diseaseCitations {
        ...Citations
      }
      findings {
        finding {
          id
        }
        sourceQFIndex
        absolutePredictiveValue
        likelihoodRatio
      }
      score
      scores {
        postClusterAdjustment
        sortType
      }
      urgency
      diseaseGivenIndependentFindings
      diseaseGivenDependentFindings
      inTopCRPerSortType
      missingPresentingSymptoms
      presentableDisease
      patientIncidence
      flags {
        ...Flag
      }
    }
    clustersToConsider {
      id
      crIds
      sortType
      scores {
        adjustedNormalized
      }
      title {
        ...QualifiedFeature
        urgencyInfo: feature {
          id
          urgency
          urgencyId
        }
      }
      bestCrId
      flags
      urgencyId
    }
    options
  }
  ${Flag}
  ${QualifiedFeature}
  ${Citations}
`;

export const ConversationNextSuggestionsFragment = gql`
  fragment ConversationNextSuggestions on PatientSuggestions {
    clusters {
      id
      crIds
      sortType
      title {
        ...QualifiedFeature
      }
    }
    diseases {
      disease {
        id
        name
        feature {
          urgencyId
        }
      }
      complicationOf {
        id
        name
      }
      complicationOfObj {
        etiologies {
          disease {
            name
            id
          }
          sensitivity
        }
      }
      crId
      prob
      inTopCRPerSortType
      selectedBySortType
      patientIncidence
      diseaseCitations {
        ...Citations @include(if: $debug)
      }
      crFindings {
        finding {
          ...QualifiedFeature
        }
        gfinding {
          ...GroupedFeatureResponse
        }
        score
        absolutePredictiveValue
        likelihoodRatio
      }
    }
    suggestions {
      suggestion {
        ...QualifiedFeature
      }
      category
      entropy
      mutualInformation
      mutualInformationUrgencyDifferentiation
      ...PatientSuggestionMIExplanations @include(if: $debug)
    }
    debugReport @include(if: $debug)
  }
  ${Citations}
  ${GroupedFeatureResponse}
`;

/**
 * @deprecated
 */
export const Graph = gql`
  fragment Graph on Graph {
    nodes {
      id
      qfs {
        ...QualifiedFeature
      }
      probability
      percentRange {
        min
        max
      }
      isConnected
      type
      sourceQFIndex
      sourceQF {
        ...QualifiedFeature
      }
    }
    edges {
      id
      sourceID
      targetID
      prob
      lift
      type
      usedInComputation
      ruleValueId
    }
  }
  ${QualifiedFeature}
`;
